import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Flame, Download, Share2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Brutalist share card for a streak milestone. Pops the user's project domain,
 * trailing streak count and current score into a 16:9-ish image they can export
 * to PNG or post straight to X.
 */
export default function StreakShareCard({ open, onOpenChange, domain, name, streak, score }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const safeName = (name || domain || "project").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  const milestoneNote =
    streak >= 12 ? "Quarter-year of weekly wins. Compounding works." :
    streak >= 6  ? "Half a quarter of nonstop weekly score gains." :
    "Three weeks of weekly score gains. Keep shipping.";

  const exportPng = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#09090b", scale: 2, useCORS: true, logging: false,
      });
      const link = document.createElement("a");
      link.download = `arloseo-streak-${safeName}-${streak}w.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Share card downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setBusy(false);
    }
  };

  const tweetIntent = () => {
    const url = "https://arloseo.app/?utm_source=twitter&utm_medium=streak_share&utm_campaign=milestone";
    const text = `🔥 ${streak} weeks of rising SEO scores on ${domain} — tracked with @arloseo.\n\nCurrent score: ${score}/100.`;
    const href = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl">
            Milestone unlocked.
          </DialogTitle>
          <DialogDescription>
            {streak}-week streak on <span className="font-mono-data text-foreground">{domain}</span>. Share the win.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          {/* The PNG card */}
          <div
            ref={cardRef}
            data-testid="streak-share-card-preview"
            className="relative w-full aspect-[16/9] overflow-hidden bg-foreground text-background p-7"
            style={{ fontFamily: "Georgia, serif" }}
          >
            {/* dotted grid bg */}
            <div
              className="absolute inset-0 opacity-[0.12]"
              style={{
                backgroundImage:
                  "radial-gradient(circle, #ffffff 1px, transparent 1px)",
                backgroundSize: "16px 16px",
              }}
            />
            <div className="relative h-full flex flex-col justify-between">
              {/* top: brand + kicker */}
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold tracking-tight text-base">ARLOSEO</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-60 mt-0.5">
                    // streak milestone
                  </div>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-60 text-right">
                  {domain}
                </div>
              </div>

              {/* middle: huge streak */}
              <div className="flex items-end gap-4">
                <Flame className="w-16 h-16 text-amber-400" strokeWidth={1.5} fill="currentColor" />
                <div>
                  <div
                    className="font-semibold leading-none tracking-tight"
                    style={{ fontSize: "92px" }}
                  >
                    {streak}W
                  </div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] opacity-70 mt-1">
                    weekly score streak
                  </div>
                </div>
                <div className="ml-auto text-right">
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-60">
                    Current
                  </div>
                  <div className="font-semibold tracking-tight text-4xl mt-1">
                    {score}<span className="opacity-50 text-xl">/100</span>
                  </div>
                </div>
              </div>

              {/* bottom: caption */}
              <div className="pt-3 border-t border-white/15 flex items-end justify-between gap-4">
                <div className="text-xs opacity-80 max-w-[70%] leading-snug" style={{ fontFamily: "Georgia, serif" }}>
                  {milestoneNote}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-60">
                  arloseo.app
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              onClick={exportPng}
              disabled={busy}
              className="rounded-none h-10 bg-foreground hover:bg-foreground/90"
              data-testid="streak-share-export-png"
            >
              <Download className="w-4 h-4 mr-1.5" /> {busy ? "Rendering…" : "Export PNG"}
            </Button>
            <Button
              onClick={tweetIntent}
              variant="outline"
              className="rounded-none h-10"
              data-testid="streak-share-tweet"
            >
              <Share2 className="w-4 h-4 mr-1.5" /> Tweet streak
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
